import { useRef, useState } from "react";
import { FileText, Loader2, ScanLine, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/common";
import { api, errorMessage } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { fmtDateTime } from "@/lib/format";
import { compressImage, detectType } from "@/lib/upload";
import { cn } from "@/lib/utils";
import { openFile } from "@/components/photos/SecureImage";
import { DeliveryNoteVerify } from "./DeliveryNoteVerify";

const toBase64 = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(",")[1]); r.onerror = reject; r.readAsDataURL(file); });

export function DeliveryNoteScanner({ purchaseOrderId, onApplied, onScanned, className, testId = "dn-scan" }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const scan = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const type = detectType(file);
      let payload = file;
      if (type.startsWith("image/")) {
        payload = await compressImage(file, 2200, 0.9);
        if (!["image/jpeg", "image/png", "image/webp"].includes(detectType(payload))) throw new Error("Photo JPG, PNG ou WEBP attendue");
        setPreview(URL.createObjectURL(payload));
      } else if (type === "application/pdf") {
        setPreview(null);
      } else {
        throw new Error("Photo (JPG / PNG / WEBP) ou PDF attendu");
      }
      const { data } = await api.post("/parts/delivery-notes/scan", { image_base64: await toBase64(payload), content_type: detectType(payload), purchase_order_id: purchaseOrderId || null });
      setResult(data);
      onScanned?.(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const onPick = (e) => {
    scan(e.target.files?.[0]);
    e.target.value = "";
  };

  return (
    <>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid={`${testId}-camera-input`} />
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} data-testid={`${testId}-file-input`} />
      <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid={testId}>
        <Button type="button" variant="outline" disabled={busy} onClick={() => camRef.current?.click()} className="rounded-full border-primary/50 text-primary hover:bg-primary/10" data-testid={`${testId}-scan-button`}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ScanLine className="mr-1 h-4 w-4" />} {busy ? "Lecture en cours…" : "Scanner un bon de livraison"}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`${testId}-file-button`}><Upload className="mr-1 h-4 w-4" /> PDF / photo</Button>
      </div>
      {result && <DeliveryNoteVerify result={result} preview={preview} onClose={() => setResult(null)} onApplied={(summary) => { setResult(null); onApplied?.(summary); }} testId={testId} />}
    </>
  );
}

const STATUS = { pending: ["En attente", "border-amber-500/40 text-amber-500"], applied: ["Intégré au stock", "border-emerald-500/40 text-emerald-500"] };

export function DeliveryNotesHistory({ refreshKey }) {
  const { data } = useFetch(() => api.get("/parts/delivery-notes"), [refreshKey]);
  if (!data?.length) return null;
  return (
    <div className="mt-6" data-testid="dn-history">
      <div className="eyebrow mb-2">Bons de livraison scannés</div>
      <div className="space-y-1.5">
        {data.map((n) => {
          const [label, cls] = STATUS[n.status] || [n.status, ""];
          return (
            <div key={n.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm" data-testid={`dn-history-${n.id}`}>
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="font-semibold">{n.note_number || "Sans numéro"}</span><span className="text-muted-foreground">· {n.supplier_name || "Fournisseur inconnu"}{n.order_number ? ` · ${n.order_number}` : ""}</span></div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{n.lines_count} ligne{n.lines_count > 1 ? "s" : ""}{n.result ? ` · ${n.result.stock_updated} en stock, ${n.result.created} créé(s)` : ""}</span>
                <span>{fmtDateTime(n.created_at)}{n.applied_by ? ` · ${n.applied_by}` : ""}</span>
                <Pill className={cls}>{label}</Pill>
                {n.file_id && <Button size="sm" variant="ghost" onClick={() => openFile(n.file_id)} data-testid={`dn-history-open-${n.id}`}>Voir le scan</Button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
